import { generateRoutes } from '../../api/system'
import { constantRoutes } from '../../router'
import Layout from '../../layout/index'

let routeGeneration = 0
let pendingRouteRequest = null

const permission = {
  namespaced: true,
  state: {
    // 侧边栏
    sidebarRouters: []
  },
  mutations: {
    SET_ROUTES (state, sidebarRouters) {
      state.sidebarRouters = sidebarRouters
    },
    RESET_ROUTES (state) {
      state.sidebarRouters = []
    }
  },
  // 异步处理方式
  actions: {
    /**
     * 获取用户菜单信息
     */
    generateRoutes ({ commit, rootGetters }) {
      const generation = routeGeneration
      if (pendingRouteRequest && pendingRouteRequest.generation === generation) {
        return pendingRouteRequest.promise
      }

      const request = generateRoutes().then(res => {
        // 退出登录或切换账号后，丢弃上一个账号尚未完成的菜单请求。
        if (generation !== routeGeneration) return []

        // 管理员菜单由后端角色配置决定；非管理员采用显式客户菜单白名单，
        // 即使后端菜单配置或缓存异常也不会把管理菜单暴露到客户侧。
        const availableRoutes = filterBusinessRoutes(res, rootGetters.roles !== 'ROLE_ADMIN')
        const routeData = JSON.parse(JSON.stringify(availableRoutes))
        const sidebarData = JSON.parse(JSON.stringify(availableRoutes))
        // 根据组件的实际路径完成拼接动作
        const rewriteRoutes = filterAsyncRouter(routeData)
        const sidebarRoutes = filterAsyncRouter(sidebarData)
        // 将没有匹配到的路由全部替换为404
        rewriteRoutes.push({ path: '*', redirect: '/404', hidden: true })
        // 拼接公共路由后设置当前用户角色到Vuex中
        // 使用说明固定在首页之后；推广模块暂时仅向管理员展示。
        const publicSidebarRoutes = constantRoutes.map(route => {
          if (route.name === 'CustomerGuideLayout') return { ...route, hidden: false }
          if (route.name === 'ReferralLayout') return { ...route, hidden: rootGetters.roles !== 'ROLE_ADMIN' }
          return route
        })
        commit('SET_ROUTES', publicSidebarRoutes.concat(sidebarRoutes))
        return rewriteRoutes
      })

      const trackedRequest = request.then(routes => {
        if (pendingRouteRequest && pendingRouteRequest.promise === trackedRequest) pendingRouteRequest = null
        return routes
      }, error => {
        if (pendingRouteRequest && pendingRouteRequest.promise === trackedRequest) pendingRouteRequest = null
        throw error
      })
      pendingRouteRequest = { generation, promise: trackedRequest }
      return trackedRequest
    },
    resetRoutes ({ commit }) {
      routeGeneration += 1
      pendingRouteRequest = null
      commit('RESET_ROUTES')
    }
  }
}

const CUSTOMER_ROUTE_PATHS = new Set([
  'transport',
  'tr-create',
  'tr-quick-quote',
  'tr-orders',
  'tr-claims',
  'tr-disputes',
  'finance',
  'fin-accounts',
  'fin-deposits',
  'fin-ledger',
  'portal-settings',
  'portal-contacts',
  'portal-packages',
  'portal-profile'
])

function filterBusinessRoutes (routes, isCustomer) {
  if (!Array.isArray(routes)) return []

  return routes.reduce((filteredRoutes, route) => {
    const path = String(route.path || '').split('/').filter(Boolean).pop() || ''
    if (path === 'fin-bills' || (isCustomer && !CUSTOMER_ROUTE_PATHS.has(path))) return filteredRoutes

    const filteredRoute = { ...route }
    if (Array.isArray(route.children)) {
      filteredRoute.children = filterBusinessRoutes(route.children, isCustomer)
      // 不展示被白名单过滤后已经没有任何入口的目录。
      if (isCustomer && route.children.length > 0 && filteredRoute.children.length === 0) return filteredRoutes
    }
    filteredRoutes.push(filteredRoute)
    return filteredRoutes
  }, [])
}

/**
 * 对异步得到的路由做过滤的动作
 */
function filterAsyncRouter (asyncRouterMap) {
  return asyncRouterMap.filter(route => {
    // 在component存在的情况下才能够进行处理
    if (route.component) {
      // 如果当前路由的组件是Layout
      if (route.component === 'Layout') {
        route.component = Layout
      } else {
        // 非父菜单组件都到views目录下加载
        route.component = loadComponent(route.component)
      }
    }
    // 如果当前路由含有子路由
    if (route.children !== null && route.children && route.children.length > 0) {
      // 递归加载子路由
      route.children = filterAsyncRouter(route.children)
    }
    return true
  })
}

/**
 * 根据组件路径加载对应组件
 *
 * @param {*} componentPath
 */
function loadComponent (componentPath) {
  return (resolve, reject) => require([`@/views/${componentPath}`], resolve, reject)
}

export default permission
